import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import { Form } from '@/components_deprecated/Form'
import styled from 'styled-components'
import {
  differenceInMinutes,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  parseISO,
} from 'date-fns'
import type { ApproveWorkPeriodActivityNode } from './types.graphql'
import { Checkbox, DatePicker, TimePicker } from '@/components'
import { Textarea } from '@/components/ui/textarea'
import { isMobile } from 'react-device-detect'
import { useActivityColor } from '@/utils'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`

const ErrorText = styled.span`
  grid-column: 1 / 3;
  color: ${props => props.theme.colors.red};
  font-size: 0.85rem;
`

export interface ExcludeActivityPromptData {
  excludeFromSalaryCalculation: boolean
  excludeBefore: Date | null
  excludeAfter: Date | null
  excludeComment: string
}

interface ExcludeActivityPromptProps {
  activity: ApproveWorkPeriodActivityNode
  onSubmit: (data: ExcludeActivityPromptData | null) => void
}

export const ExcludeActivityPrompt: React.FC<ExcludeActivityPromptProps> = ({
  activity,
  onSubmit,
}) => {
  const activityStart = parseISO(activity.datetimeStart)
  const activityEnd = parseISO(activity.datetimeEnd)
  const activityIsSameDay = isSameDay(activityStart, activityEnd)
  const initialExcludeBeforeString =
    activity.excludeFromSalaryCalculation && activity.excludeBefore
      ? activity.excludeBefore
      : activity.datetimeStart
  const initialExcludeAfterString =
    activity.excludeFromSalaryCalculation && activity.excludeAfter
      ? activity.excludeAfter
      : activity.datetimeEnd
  const initialExcludeBefore = parseISO(initialExcludeBeforeString)
  const initialExcludeAfter = parseISO(initialExcludeAfterString)
  const getActivityColor = useActivityColor()
  const translations = useTranslate({
    label: {
      excludeFromSalaryCalculation: 'gantt.remove-from-salary-calculation',
      excludeBefore: 'common.exclude-before',
      excludeAfter: 'common.exclude-after',
      salaryCode: 'common.salary-code',
      amount: 'common.amount',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      excludeBeforeRange: 'salary.exclude-activity.error.exclude-before-range',
      excludeAfterRange: 'salary.exclude-activity.error.exclude-after-range',
      required: 'common.required',
    },
  })
  const { formValues, formErrors, formValid, updateForm, validateForm } =
    useForm<ExcludeActivityPromptData>({
      values: {
        excludeFromSalaryCalculation: activity.excludeFromSalaryCalculation,
        excludeBefore: activity.excludeBefore ? initialExcludeBefore : null,
        excludeAfter: activity.excludeAfter ? initialExcludeAfter : null,
        excludeComment: activity.excludeComment ?? '',
      },
      config: {
        validateImmediately: true,
      },
      validators: {
        excludeBefore: (value, others) => {
          if (!value) return null
          if (
            isBefore(value, activityStart) ||
            isAfter(value, activityEnd) ||
            (!!others.excludeAfter && isAfter(value, others.excludeAfter))
          ) {
            return translations.error.excludeBeforeRange
          }
          return null
        },
        excludeAfter: (value, others) => {
          if (!value) return null
          if (
            isBefore(value, activityStart) ||
            isAfter(value, activityEnd) ||
            (!!others.excludeBefore && isBefore(value, others.excludeBefore))
          ) {
            return translations.error.excludeAfterRange
          }
          return null
        },
      },
    })

  function handleSubmit() {
    if (!formValues.excludeFromSalaryCalculation) {
      onSubmit({
        excludeFromSalaryCalculation: false,
        excludeBefore: null,
        excludeAfter: null,
        excludeComment: '',
      })
      return
    }
    if (!validateForm().valid) {
      return
    }
    let returnBefore = formValues.excludeBefore
    let returnAfter = formValues.excludeAfter
    if (formValues.excludeBefore && formValues.excludeAfter) {
      if (isEqual(formValues.excludeBefore, activityStart)) {
        returnBefore = null
      }
      if (isEqual(formValues.excludeAfter, activityEnd)) {
        returnAfter = null
      }
    }
    onSubmit({
      ...formValues,
      excludeBefore: returnBefore,
      excludeAfter: returnAfter,
    })
  }

  const excludeBeforeComparisonValue = formValues.excludeBefore ?? activityStart
  const excludeAfterComparisonValue = formValues.excludeAfter ?? activityEnd
  const totalActivityMinutes = differenceInMinutes(activityEnd, activityStart)
  const clampPercentage = (value: number) => Math.min(Math.max(value, 0), 1)
  const getPercentageOfActivity = (minutes: number) => {
    if (totalActivityMinutes <= 0) return 0
    return clampPercentage(minutes / totalActivityMinutes)
  }

  const excludeBeforePercentage = !formValues.excludeBefore
    ? 0
    : getPercentageOfActivity(
        differenceInMinutes(excludeBeforeComparisonValue, activityStart)
      )

  const excludeAfterPercentage = !formValues.excludeAfter
    ? 0
    : getPercentageOfActivity(
        differenceInMinutes(activityEnd, excludeAfterComparisonValue)
      )

  const includedActivityPercentage = clampPercentage(
    1 - excludeAfterPercentage - excludeBeforePercentage
  )

  return (
    <RegularModal
      title={translations.label.excludeFromSalaryCalculation}
      submitText={translations.update}
      width={isMobile ? '90%' : '600px'}
      submitDisabled={formValues.excludeFromSalaryCalculation && !formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={() => handleSubmit()}
    >
      <StyledForm preventDefault onSubmit={() => handleSubmit()}>
        <Field>
          <label>{translations.label.excludeFromSalaryCalculation}</label>
          <Checkbox
            value={formValues.excludeFromSalaryCalculation}
            onChange={value =>
              updateForm({ excludeFromSalaryCalculation: !!value })
            }
          />
        </Field>
        {formValues.excludeFromSalaryCalculation && (
          <>
            <DateTimeField>
              <label>{translations.label.excludeBefore}</label>
              <TimePicker
                value={formValues.excludeBefore ?? initialExcludeBefore}
                onChange={value => {
                  if (!value) return
                  updateForm(
                    {
                      excludeBefore: value,
                    },
                    { includeValidation: ['excludeAfter'] }
                  )
                }}
              />
              {!activityIsSameDay && (
                <DatePicker
                  mode="single"
                  clearable={false}
                  value={formValues.excludeBefore}
                  onChange={value => {
                    const newExcludeBefore = value ? new Date(value) : null
                    if (!newExcludeBefore) return
                    const currentExcludeBefore =
                      formValues.excludeBefore ?? initialExcludeBefore
                    newExcludeBefore.setHours(
                      currentExcludeBefore.getHours(),
                      currentExcludeBefore.getMinutes(),
                      currentExcludeBefore.getSeconds(),
                      currentExcludeBefore.getMilliseconds()
                    )
                    updateForm(
                      {
                        excludeBefore: newExcludeBefore,
                      },
                      { includeValidation: ['excludeAfter'] }
                    )
                  }}
                />
              )}
              {formErrors.excludeBefore && (
                <ErrorText>{formErrors.excludeBefore}</ErrorText>
              )}
            </DateTimeField>
            <DateTimeField>
              <label>{translations.label.excludeAfter}</label>

              <TimePicker
                value={formValues.excludeAfter ?? initialExcludeAfter}
                onChange={value => {
                  const newExcludeAfter = value
                  if (!newExcludeAfter) return
                  updateForm(
                    {
                      excludeAfter: newExcludeAfter,
                    },
                    { includeValidation: ['excludeBefore'] }
                  )
                }}
              />
              {!activityIsSameDay && (
                <DatePicker
                  mode="single"
                  clearable={false}
                  value={formValues.excludeAfter}
                  onChange={value => {
                    const newExcludeAfter = value ? new Date(value) : null
                    if (!newExcludeAfter) return
                    const currentExcludeAfter =
                      formValues.excludeAfter ?? initialExcludeAfter
                    newExcludeAfter.setHours(
                      currentExcludeAfter.getHours(),
                      currentExcludeAfter.getMinutes(),
                      currentExcludeAfter.getSeconds(),
                      currentExcludeAfter.getMilliseconds()
                    )
                    updateForm(
                      {
                        excludeAfter: newExcludeAfter,
                      },
                      { includeValidation: ['excludeBefore'] }
                    )
                  }}
                />
              )}
              {formErrors.excludeAfter && (
                <ErrorText>{formErrors.excludeAfter}</ErrorText>
              )}
            </DateTimeField>
            <div className="mb-2 mt-2 flex h-6 w-full overflow-hidden rounded">
              <div
                style={{
                  width: `${excludeBeforePercentage * 100}%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation: true,
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                  borderRight: '1px solid white',
                }}
              ></div>
              <div
                style={{
                  width: `${includedActivityPercentage * 100}%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation:
                      (!formValues.excludeBefore ||
                        isEqual(formValues.excludeBefore, activityStart)) &&
                      (!formValues.excludeAfter ||
                        isEqual(formValues.excludeAfter, activityEnd)),
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                }}
              ></div>
              <div
                style={{
                  width: `${excludeAfterPercentage * 100}%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation: true,
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                  borderLeft: '1px solid white',
                }}
              ></div>
            </div>
            <Field>
              <label>{translations.label.comment}</label>
              <Textarea
                value={formValues.excludeComment}
                rows={3}
                onChange={value => updateForm({ excludeComment: value })}
              />
            </Field>
          </>
        )}
      </StyledForm>
    </RegularModal>
  )
}
